"use client";

import { useContext, useEffect } from "react";

import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import IconMagnifyingGlass2 from "@ui/central-icons/icons/IconMagnifyingGlass2";
import { cn } from "@ui/lib/utils";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useTranslations } from "next-intl";
import { ComboBox, ComboBoxStateContext, Input } from "react-aria-components";

import { ListBox, type ItemBase, type SectionBase } from "@components/core/ListBox";

interface SearchComboBoxProps<Item extends ItemBase> {
  "aria-label": string;
  inputValue: string;
  onInputChange: (value: string) => void;
  onClear?: () => void;
  onSelectionChange?: (item: Item | undefined) => void;
  onItemAction?: (item: Item) => void;
  selectionMode?: "single" | "multiple" | "none";
  placeholder?: string;
  items?: Item[];
  sections?: SectionBase<Item>[];
  children?: (item: Item) => React.ReactNode;
  className?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  // TODO: make this the default behaviour once confirmed it looks good across all usages
  /** Reduces input height to 48px on mobile (lg+ stays at 60px). */
  compact?: boolean;
}

/**
 * Renders nothing. Opens the ComboBox popover when async items arrive,
 * when loading begins, or when an empty state should display.
 */
function PopoverController({
  itemCount,
  hasInput,
  isLoading,
  showEmptyState,
}: {
  itemCount: number;
  hasInput: boolean;
  isLoading: boolean;
  showEmptyState: boolean;
}) {
  const state = useContext(ComboBoxStateContext);

  useEffect(() => {
    if (!hasInput || !state || state.isOpen || !state.isFocused) return;
    if (itemCount > 0 || isLoading || showEmptyState) {
      state.open(null, "manual");
    }
  }, [itemCount, hasInput, isLoading, showEmptyState, state]);

  return null;
}

function SearchComboBox<Item extends ItemBase>({
  "aria-label": ariaLabel,
  inputValue,
  onInputChange,
  onClear,
  onSelectionChange,
  onItemAction,
  selectionMode,
  placeholder,
  items,
  sections,
  children,
  className,
  isLoading,
  loadingMessage,
  emptyMessage,
  compact,
}: SearchComboBoxProps<Item>) {
  const t = useTranslations("ClientKeys.Globals");
  const allItems = sections ? sections.flatMap(s => s.items) : (items ?? []);
  const showEmptyState = inputValue.length > 0 && allItems.length === 0 && !isLoading && !!emptyMessage;
  const allowsEmptyCollection = isLoading || showEmptyState;

  const withActions = <T extends Item>(list: T[]): T[] => {
    if (!onItemAction) return list;
    return list.map(item => ({
      ...item,
      action: item.action ?? (() => onItemAction(item)),
    }));
  };

  const renderEmptyState = allowsEmptyCollection
    ? () => (
        <div className='tw-px-2.5 tw-py-3 tw-text-sm tw-text-gray-500'>{isLoading ? loadingMessage : emptyMessage}</div>
      )
    : undefined;

  return (
    <ComboBox
      className='tw-w-full'
      inputValue={inputValue}
      onInputChange={onInputChange}
      allowsCustomValue
      allowsEmptyCollection={allowsEmptyCollection}
      menuTrigger='input'
      aria-label={ariaLabel}
      onSelectionChange={key => {
        if (!onSelectionChange) return;
        onSelectionChange(allItems.find(item => item.id === key));
      }}
      defaultFilter={() => true}
    >
      <PopoverController
        itemCount={allItems.length}
        hasInput={inputValue.length > 0}
        isLoading={!!isLoading}
        showEmptyState={showEmptyState}
      />
      <label
        className={cn(
          compact
            ? "tw-flex tw-h-12 lg:tw-h-[60px] tw-w-full tw-items-center tw-gap-3 tw-pl-5 tw-bg-white tw-shadow-[0_0_1.143px_0_rgba(0,0,0,0.40),_0_2px_4px_0_rgba(0,0,0,0.04)]"
            : "tw-flex tw-h-[60px] tw-w-full tw-items-center tw-gap-3 tw-pl-5 tw-bg-white tw-shadow-[0_0_1.143px_0_rgba(0,0,0,0.40),_0_2px_4px_0_rgba(0,0,0,0.04)]",
          "tw-rounded-2.5xl tw-cursor-text tw-border tw-border-transparent hover:tw-border-black/15 active:tw-border-black/25 tw-transition-colors tw-outline-none has-[:focus-visible]:tw-outline-focus",
          inputValue.length > 0 ? "" : "tw-pr-5",
          className,
        )}
      >
        <IconMagnifyingGlass2 aria-hidden className={cn(iconStrokeFix, "tw-size-5 tw-shrink-0 tw-opacity-40")} />
        <Input
          placeholder={placeholder}
          className='tw-flex-1 tw-bg-transparent tw-outline-none tw-rounded-sm tw-type-base tw-text-black'
        />
        {inputValue.length > 0 && (
          <button
            type='button'
            onClick={onClear}
            aria-label={t("clear_search")}
            className='tw-shrink-0 tw-cursor-pointer tw-opacity-60 tw-outline-none focus-visible:tw-outline-focus tw-rounded-l-sm tw-self-stretch tw-flex tw-items-center tw-px-5'
          >
            <IconCrossLarge aria-hidden className={cn(iconStrokeFix, "tw-size-4")} />
          </button>
        )}
      </label>

      {sections ? (
        <ListBox<Item>
          sections={sections.map(s => ({ ...s, items: withActions(s.items) }))}
          selectionMode={selectionMode}
          offset={8}
          className='tw-w-[--trigger-width]'
          renderEmptyState={renderEmptyState}
        >
          {children}
        </ListBox>
      ) : (
        <ListBox<Item>
          items={withActions(items ?? [])}
          selectionMode={selectionMode}
          offset={8}
          className='tw-w-[--trigger-width]'
          renderEmptyState={renderEmptyState}
        >
          {children}
        </ListBox>
      )}
    </ComboBox>
  );
}

SearchComboBox.displayName = "SearchComboBox";

export { SearchComboBox };
export type { SearchComboBoxProps };
